import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useCallback, useEffect } from 'react';

import usePubNubCatalog from '@/components/LiveEventSubscribers/hooks/usePubNubCatalog';
import { useFetchLiveCatalogConsoleItems } from '@/hooks/useFetchLiveCatalogConsoleItems';
import MessageScroller from '@/pages/BiddingConsole/components/MessageScroller/MessageScroller';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
    getCatalogAbsenteeBidders,
    getCatalogHasVideo,
    getCatalogIsCatalogOnly,
    getCatalogStartTime,
    getCatalogStatus,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import {
    getLiveCatalogBidders,
    getLiveCatalogCurrentItemId,
    getLiveCatalogCurrentItemIndex,
    getLiveCatalogStatus,
} from '@/redux/modules/console';
import {
    getItemSummariesForCatalog,
    getItemSummaryIndex,
    getItemSummaryPromoted,
    getItemSummarySellerId,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { fetchCatalogPresence, fetchLiveCatalogStatusIfNeeded } from '@/redux/modules/liveCatalogStatus';
import { emptyItemSummary } from '@/types/item/ItemSummary';
import { ItemCardSource, useTrackItemCardClicked } from '@/utils/amplitude/analytics';
import { shouldShowLive } from '@/utils/live';

import ConsoleSocialBadge from '../ConsoleSocial/ConsoleSocialBadge';
import { CurrentItemLink } from './components/CurrentItemLink/CurrentItemLink';
import { ProgressBar } from './components/ProgressBar/ProgressBar';
import { UpcomingItemLink } from './components/UpcomingItemLink/UpcomingItemLink';

type Props = {
    catalogId: number;
    className?: string;
};

const CatalogPageConsoleComponent = ({ catalogId, className }: Props) => {
    const dispatch = useAppDispatch();
    const trackItemCardClicked = useTrackItemCardClicked();

    const bidders = useAppSelector((state) => getLiveCatalogBidders(state, catalogId));
    const currentItemId = useAppSelector((state) => getLiveCatalogCurrentItemId(state, catalogId));
    const liveCatalogStatus = useAppSelector((state) => getLiveCatalogStatus(state, catalogId));
    const saleStartTs = useAppSelector((state) => getCatalogStartTime(state, catalogId));
    const absenteeBidders = useAppSelector((state) => getCatalogAbsenteeBidders(state, catalogId));
    const catalogStatus = useAppSelector((state) => getCatalogStatus(state, catalogId));
    const hasVideo = useAppSelector((state) => getCatalogHasVideo(state, catalogId));
    const isCatalogOnly = useAppSelector((state) => getCatalogIsCatalogOnly(state, catalogId));

    const items = useAppSelector((state) => getItemSummariesForCatalog(state, catalogId));

    const itemId = currentItemId || items?.[0]?.itemId || 0;
    const liveLotIndex = useAppSelector((state) => getLiveCatalogCurrentItemIndex(state, catalogId));
    const index = useAppSelector((state) => getItemSummaryIndex(state, itemId));
    const promoted = useAppSelector((state) => getItemSummaryPromoted(state, itemId));
    const sellerId = useAppSelector((state) => getItemSummarySellerId(state, itemId));
    const itemIndex = index || 0;
    const viewers = bidders + absenteeBidders || 1;

    const nextItem = items.find((i) => i.index === itemIndex + 1) || emptyItemSummary;
    const thirdItem = items.find((i) => i.index === itemIndex + 2) || emptyItemSummary;

    const showLive = shouldShowLive(catalogStatus, isCatalogOnly, saleStartTs);

    // Subscribe to PubNub presence updates for real-time bidder count
    // this also fixes the issue where the viewer count is different between console and this catalog page
    usePubNubCatalog({
        catalogId,
        subscribeToPresence: showLive,
    });

    useEffect(() => {
        if (showLive) {
            dispatch(fetchLiveCatalogStatusIfNeeded(catalogId, sellerId));
            dispatch(fetchCatalogPresence(catalogId));
        }
    }, [catalogId, dispatch, showLive, sellerId]);

    useFetchLiveCatalogConsoleItems({
        catalogId,
        currentItemId,
        liveLotIndex,
        sellerId,
        showLive,
    });

    const auctionPaused = liveCatalogStatus === 'paused';

    const handleItemClick = useCallback(
        (clickedItemId: number) => {
            trackItemCardClicked({
                catalogId,
                itemId: clickedItemId,
                promoted,
                source: ItemCardSource.CatalogPageConsole,
            });
        },
        [catalogId, promoted, trackItemCardClicked]
    );

    return (
        <div className={cn('mx-auto block w-full max-w-full flex-[1_1_394px] border-b-0', className)}>
            <div className="relative mb-xs flex h-[384px] w-full flex-row justify-between xsMax:justify-evenly">
                <div
                    className="relative z-[4] flex h-[384px] w-[calc(100%/3)] flex-col items-center justify-center p-md xsMax:w-full xsMax:border-none xsMax:p-0"
                    onClick={() => {
                        handleItemClick(itemId);
                    }}
                >
                    <CurrentItemLink
                        catalogId={catalogId}
                        itemId={itemId}
                        showLive={showLive}
                    />
                    <ConsoleSocialBadge
                        className="absolute top-xs left-xs"
                        hasVideo={hasVideo}
                        pause={auctionPaused}
                        viewers={viewers}
                    />
                    <div className="absolute bottom-[4px] left-0 z-[2] block w-[500px]">
                        <MessageScroller
                            catalogId={catalogId}
                            className="z-10 ml-0"
                            convert
                            hideMessages
                            smallConsole
                        />
                    </div>
                </div>
                {nextItem.itemId > 0 && (
                    <UpcomingItemLink
                        itemId={nextItem.itemId}
                        onClick={() => {
                            handleItemClick(nextItem.itemId);
                        }}
                    />
                )}
                {thirdItem.itemId > 0 && (
                    <UpcomingItemLink
                        itemId={thirdItem.itemId}
                        lastItem
                        onClick={() => {
                            handleItemClick(thirdItem.itemId);
                        }}
                    />
                )}
            </div>
            <ProgressBar catalogId={catalogId} />
        </div>
    );
};

export const CatalogPageConsole = CatalogPageConsoleComponent;
